import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	CircularProgress,
	IconButton,
} from "@mui/material";
import { Close as CloseIcon, Delete as DeleteIcon, ContentCopy as DuplicateIcon } from "@mui/icons-material";
import type { Job } from "../../../../../../models";
import { isFieldVisible } from "../../../../../../utilities/jobValidation";
import type { ImportTableColumn } from "../../../../../../utilities/types";
import { useJobDetailModalViewModel } from "./JobDetailModal.vm";
import type { JobDetailModalProps } from "./JobDetailModal.types";
import "./JobDetailModal.css";

// Shared components
import { JobFormFields } from "../../../../shared/JobFormFields";
import { ValidationErrorsContext } from "../../shared/hooks/ValidationErrorsContext";
import { ColumnContext } from "../../columns/standard-column-types/ColumnContext";
import { JobCardMap } from "../../mobile/JobCard/JobCardMap";

export const JobDetailModal: React.FC<JobDetailModalProps> = (props) => {
	const { open, job, onDelete, onDuplicate } = props;
	const vm = useJobDetailModalViewModel(props);

	// Don't render if closed or no job
	if (!open || !job) {
		return null;
	}

	// Get current form values as a Job object for mobile columns
	const currentJob = vm.watch() as Job;
	// Cast columnContext to ColumnContext for mobile columns (type-safe at runtime)
	const columnContext = vm.columnContext as unknown as ColumnContext;

	// Helper to check field visibility
	const isVisible = (columnId: ImportTableColumn) => isFieldVisible(columnId, vm.validationContext);

	return (
		<Dialog
			open={open}
			onClose={vm.handleClose}
			maxWidth="xl"
			fullWidth
			scroll="paper"
			className="job-detail-modal"
			aria-labelledby="job-detail-modal-title"
		>
			<DialogTitle id="job-detail-modal-title" sx={{ pr: 6 }}>
				Edit Job:{" "}
				{[currentJob.importGrowerName, currentJob.importFarmName, currentJob.importFieldName]
					.filter(Boolean)
					.join(" - ")}
				<IconButton
					aria-label="close"
					onClick={vm.handleClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<ValidationErrorsContext.Provider value={vm.validationErrors}>
				<DialogContent dividers sx={{ p: 0, display: "flex", height: "70vh" }}>
					{vm.loading && (
						<Box className="loading-overlay">
							<CircularProgress />
							<Typography>Saving...</Typography>
						</Box>
					)}

					{/* Form Fields - Left Side (Scrollable) */}
					<Box sx={{ flex: "0 0 58%", overflowY: "auto", p: 3 }}>
						<form id="job-detail-form" onSubmit={vm.handleSubmit(vm.onSubmit, vm.onError)}>
							<JobFormFields
								job={currentJob}
								columnContext={columnContext}
								dropdownOptions={{
									gridSizes: columnContext.gridSizes,
									depths: columnContext.depths,
									labPackages: columnContext.labPackages,
									micros: columnContext.micros,
								}}
								branches={props.branches}
								isVisible={isVisible}
							/>
						</form>
					</Box>

					{/* Map Preview - Right Side (Fixed) */}
					<Box sx={{ flex: 1, borderLeft: 1, borderColor: "divider", minWidth: 0 }}>
						<JobCardMap job={currentJob} height="100%" />
					</Box>
				</DialogContent>
			</ValidationErrorsContext.Provider>

			<DialogActions sx={{ justifyContent: "space-between" }}>
				<Box>
					{onDuplicate && (
						<Button
							startIcon={<DuplicateIcon />}
							onClick={() => {
								onDuplicate(job.id);
								vm.handleClose();
							}}
							disabled={vm.loading}
						>
							Duplicate
						</Button>
					)}
					{onDelete && (
						<Button
							startIcon={<DeleteIcon />}
							onClick={() => {
								const deleted = onDelete(job.id);
								if (deleted) {
									vm.handleClose();
								}
							}}
							color="error"
							disabled={vm.loading}
						>
							Delete
						</Button>
					)}
				</Box>
				<Box>
					<Button onClick={vm.handleClose} color="inherit">
						Cancel
					</Button>
					<Button
						type="submit"
						form="job-detail-form"
						variant="contained"
						color="primary"
						disabled={vm.loading || Object.keys(vm.validationErrors).length > 0}
					>
						{vm.loading ? "Saving..." : "Save Changes"}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};
